import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ChevronRight, Home } from "lucide-react";

// Route to label mapping
const routeLabels: Record<string, string> = {
  "": "Home",
  "services": "Services",
  "about": "About",
  "faq": "FAQ",
  "service-areas": "Service Areas",
  "concrete-services": "Concrete Services",
  "waterproofing-services": "Waterproofing",
  "basement-waterproofing": "Basement Waterproofing",
  "paving-services": "Paving Services",
  "paver-patios": "Paver Patios",
  "paver-patio-repair": "Paver Patio Repair",
  "paver-walkways": "Paver Walkways",
  "paver-driveways": "Paver Driveways",
  "retaining-walls": "Retaining Walls",
  "retaining-wall-repair": "Retaining Wall Repair",
  "steps-stoops": "Steps & Stoops",
  "outdoor-kitchens": "Outdoor Kitchens",
  "fireplaces-firepits": "Fire Pits",
  "fire-pits": "Fire Pits",
  "fireplace-installation": "Fireplace Installation",
  "stone-veneer": "Stone Veneer",
  "stone-masonry": "Stone Masonry",
  "stone-masonry-repair": "Stone Masonry Repair",
  "brick-work": "Brick Work",
  "block-work": "Block Work",
  "custom-masonry": "Custom Masonry",
  "repair": "Repair",
  "steps-stoops-repair": "Steps & Stoops Repair",
  
  "patios": "Patios",
  "driveways": "Driveways",
  "sidewalks": "Sidewalks",
  "stamped-concrete": "Stamped Concrete",
  "foundations": "Concrete Foundations",
  "foundation-repair": "Foundation Repair",
  "commercial-masonry": "Commercial Masonry & Concrete",
  "patio-waterproofing": "Patio Waterproofing",
  "pro-tips": "Pro Tips",
  "packages": "Project Packages",
  "choosing-a-professional-masonry-company": "Choosing a Professional Masonry Company",
  "benefits-of-adding-a-paver-patio": "Benefits of Adding a Paver Patio",
  "paver-patio-cost-north-jersey": "Paver Patio Cost in North Jersey",
  "passaic": "Passaic",
};

const BASE_URL = "https://masonry-pro.com";

export function Breadcrumb() {
  const location = useLocation();
  const pathSegments = location.pathname.split("/").filter(Boolean);

  // Don't render on homepage
  if (pathSegments.length === 0) {
    return null;
  }

  // For masonry-services child pages, skip the masonry-services segment 
  // so breadcrumb reads Home > [Service Name] and links to / instead of /masonry-services
  const isMasonryChild = pathSegments[0] === "masonry-services" && pathSegments.length > 1;
  const displaySegments = isMasonryChild ? pathSegments.slice(1) : pathSegments;

  const breadcrumbs = displaySegments.map((segment, index) => {
    // For masonry child pages, build path using original segments
    const path = isMasonryChild
      ? "/" + pathSegments.slice(0, index + 2).join("/")
      : "/" + pathSegments.slice(0, index + 1).join("/");
    const label = routeLabels[segment] || segment.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
    const isLast = index === displaySegments.length - 1;

    return { path, label, isLast };
  });

  // Build JSON-LD BreadcrumbList schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": BASE_URL
      },
      ...breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        "position": index + 2,
        "name": crumb.label,
        "item": `${BASE_URL}${crumb.path}`
      }))
    ]
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      
      <nav 
        aria-label="Breadcrumb" 
        className="bg-primary border-b border-primary/80 pt-28 md:pt-32 sticky top-0 z-40"
      >
        <div className="container mx-auto px-4">
          <ol className="flex items-center gap-2 py-4 text-base">
            <li>
              <Link 
                to="/" 
                className="flex items-center gap-1 text-primary-foreground/70 hover:text-secondary transition-colors"
              >
                <Home className="h-5 w-5" />
                <span className="sr-only">Home</span>
              </Link>
            </li>
            {breadcrumbs.map((crumb) => (
              <li key={crumb.path} className="flex items-center gap-2">
                <ChevronRight className="h-5 w-5 text-primary-foreground/40" />
                {crumb.isLast ? (
                  <span className="text-secondary font-semibold">{crumb.label}</span>
                ) : (
                  <Link 
                    to={crumb.path} 
                    className="text-primary-foreground/70 hover:text-secondary transition-colors"
                  >
                    {crumb.label}
                  </Link>
                )}
              </li>
            ))}
          </ol>
        </div>
      </nav>
    </>
  );
}
